// ============================================================
//  SIGNEDOFF
//  Apprentice Evidence Tracker
//  v2.2 Modular Vite Build — with Router
// ============================================================

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

// Hooks
import { useAuth } from './hooks/useAuth';
import { useUnits } from './hooks/useUnits';
import { useEvidence } from './hooks/useEvidence';
import { useSignoffs } from './hooks/useSignoffs';
import { useTrainingPlan } from './hooks/useTrainingPlan';
import { useTradeSchool } from './hooks/useTradeSchool';
import { useApprentices } from './hooks/useApprentices';
import { useSites } from './hooks/useSites';

// PDF
import { generateEvidencePDF } from './lib/pdf';

// Layout
import { Topbar } from './components/layout/Topbar';
import { Sidebar } from './components/layout/Sidebar';
import { BottomNav } from './components/layout/BottomNav';

// Common
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ToastProvider, useToast } from './components/common/Toast';

// Modals
import { AddEvidenceModal } from './components/evidence/AddEvidenceModal';

// Pages
import { LoginPage } from './pages/LoginPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { AuthConfirmPage } from './pages/AuthConfirmPage';
import { DashboardPage } from './pages/DashboardPage';
import { EvidenceLogPage } from './pages/EvidenceLogPage';
import { EvidenceReviewPage } from './pages/EvidenceReviewPage';
import { UnitProgressPage } from './pages/UnitProgressPage';
import { WorkplaceSignoffPage } from './pages/WorkplaceSignoffPage';
import { TradeSchoolPage } from './pages/TradeSchoolPage';
import { TrainingPlanPage } from './pages/TrainingPlanPage';
import { ApprenticesPage } from './pages/ApprenticesPage';
import { ReportsPage } from './pages/ReportsPage';
import { AccountPage } from './pages/AccountPage';
import { BulkUploadPage } from './pages/BulkUploadPage';
import { HelpPage } from './pages/HelpPage';
import { FeedbackPage } from './pages/FeedbackPage';


// Route-to-nav mapping
const ROUTE_MAP = {
  '/': 'dashboard',
  '/evidence': 'evidence',
  '/units': 'units',
  '/workplace-signoff': 'workplacesignoff',
  '/tradeschool': 'tradeschool',
  '/review': 'review',
  '/training-plan': 'trainingplan',
  '/bulk-upload': 'bulkupload',
  '/reports': 'reports',
  '/apprentices': 'apprentices',
  '/help': 'help',
  '/feedback': 'feedback',
  '/account': 'account',
};

const NAV_TO_ROUTE = Object.fromEntries(
  Object.entries(ROUTE_MAP).map(([route, id]) => [id, route])
);

export default function App() {
  return (
    <ToastProvider>
      <ErrorBoundary>
        <AppInner />
      </ErrorBoundary>
    </ToastProvider>
  );
}

function AppInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  /**
   * Wrap a data-changing call so failures are always visible.
   * Nearly every mutation in this app used to fail into console.log or nothing at
   * all, which is how apprentices lost drafts without ever seeing a warning.
   * The error is re-thrown so pages that show their own inline message still can.
   */
  const withToast = useCallback((fn, { done, failed }) => async (...args) => {
    try {
      const result = await fn(...args);
      if (done) toast.success(done);
      return result;
    } catch (e) {
      console.error(failed, e);
      toast.error(`${failed} — ${e?.message || e}`);
      throw e;
    }
  }, [toast]);

  // ── Auth ──
  const auth = useAuth();
  const { session, profile, loading: authLoading, login, logout, setPassword, sendPasswordReset, updateProfile } = auth;

  // ── Data hooks ──
  const orgId = profile?.org_id;
  const { units, allUnits, getUnitName, getUnit, loading: unitsLoading } = useUnits();
  const { evidence, addEvidence, saveDraft, updateDraft, updateEvidence, deleteEvidence, loading: evidenceLoading } = useEvidence(profile?.id, orgId);
  const { unitSignoffs, workplaceSignoffs, knowledgeUnits, levelDates, toggleUnitSignoff, toggleWorkplaceSignoff, toggleKnowledge, saveLevelDate, loading: signoffsLoading } = useSignoffs(orgId);
  const { plans: trainingPlans, upsertPlan, loading: plansLoading } = useTrainingPlan(orgId);
  const { tradeSchoolLogs, kanganLogs, addTradeSchoolLog, deleteTradeSchoolLog, addKanganLog, deleteKanganLog, loading: tsLoading } = useTradeSchool(orgId);
  const { allProfiles, getMyApprentices, getArchivedApprentices, inviteUser, getSignInLink, deleteApprentice, setArchived, assignTrainer, updateUser, loading: profilesLoading } = useApprentices(profile, orgId);
  const { sites, addSite, updateSite, deleteSite, getSiteName } = useSites(orgId);

  // ── UI state ──
  const [showAdd, setShowAdd] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  // Unit pre-selected when logging from a specific unit on the dashboard
  const [presetUnit, setPresetUnit] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [pdfProgress, setPdfProgress] = useState(null);

  // ── Current view from URL ──
  const currentView = ROUTE_MAP[location.pathname] || 'dashboard';

  // Navigation handler — updates URL instead of state
  const setView = useCallback((id) => {
    const route = NAV_TO_ROUTE[id] || '/';
    navigate(route);
  }, [navigate]);

  // ── Apprentice selection ──
  const myApprentices = getMyApprentices();
  const archivedApprentices = getArchivedApprentices();
  const archivedIds = useMemo(
    () => new Set(allProfiles.filter(p => p.archived_at).map(p => p.id)),
    [allProfiles],
  );
  const [selApprId, setSelApprId] = useState(null);

  const activeApprId = useMemo(() => {
    if (profile?.role === 'apprentice') return profile.id;
    if (selApprId && myApprentices.find(a => a.id === selApprId)) return selApprId;
    if (myApprentices.length > 0) return myApprentices[0].id;
    return null;
  }, [profile, selApprId, myApprentices]);

  useEffect(() => {
    if (activeApprId && activeApprId !== selApprId && profile?.role !== 'apprentice') {
      setSelApprId(activeApprId);
    }
  }, [activeApprId, selApprId, profile?.role]);

  // ── Combined loading state ──
  const dataLoading = orgId && (unitsLoading || evidenceLoading || signoffsLoading || plansLoading || tsLoading || profilesLoading);

  // ── FIX #14: Role-based evidence filtering ──
  // Archived people drop out of the day-to-day views — no stale entries sitting
  // in the review queue for someone who left. Reports still get the full set.
  const filteredEvidence = useMemo(() => {
    if (!profile) return [];
    if (profile.role === 'apprentice') {
      return evidence.filter(e => e.apprentice_id === profile.id);
    }
    // Trainers/employers see everything except other people's drafts
    return evidence.filter(e => e.status !== 'draft' && !archivedIds.has(e.apprentice_id));
  }, [evidence, profile, archivedIds]);

  // Everything an archived apprentice ever logged, for reporting after they go
  const evidenceForReports = useMemo(() => {
    if (!profile) return [];
    if (profile.role === 'apprentice') return evidence.filter(e => e.apprentice_id === profile.id);
    return evidence.filter(e => e.status !== 'draft');
  }, [evidence, profile]);

  // ── Handlers ──
  const handleApprove = useCallback(withToast(async (id, note) => {
    await updateEvidence(id, { status: 'approved', review_note: note || null, reviewed_by: profile.id, reviewed_at: new Date().toISOString() });
  }, { done: 'Evidence approved.', failed: "Couldn't approve that entry" }), [updateEvidence, profile, withToast]);

  const handleReject = useCallback(withToast(async (id, note) => {
    await updateEvidence(id, { status: 'rejected', review_note: note || null, reviewed_by: profile.id, reviewed_at: new Date().toISOString() });
  }, { done: 'Sent back to the apprentice.', failed: "Couldn't return that entry" }), [updateEvidence, profile, withToast]);

  const handleDeleteEvidence = useCallback(withToast(deleteEvidence, {
    done: 'Entry deleted.', failed: "Couldn't delete that entry",
  }), [deleteEvidence, withToast]);

  // Edit the comment on an already-reviewed entry without disturbing its status
  // or who reviewed it — approving and commenting are different actions.
  const handleUpdateNote = useCallback(withToast(async (id, note) => {
    await updateEvidence(id, { review_note: note || null });
  }, { done: 'Comment saved.', failed: "Couldn't save that comment" }), [updateEvidence, withToast]);

  // Sign-off toggles and plan edits — all previously silent on failure
  const handleToggleSignoff = useCallback(withToast(toggleUnitSignoff, { failed: "Couldn't update the evidence sign-off" }), [toggleUnitSignoff, withToast]);
  const handleToggleWorkplace = useCallback(withToast(toggleWorkplaceSignoff, { failed: "Couldn't update the workplace sign-off" }), [toggleWorkplaceSignoff, withToast]);
  const handleToggleKnowledge = useCallback(withToast(toggleKnowledge, { failed: "Couldn't update the knowledge tick" }), [toggleKnowledge, withToast]);
  const handleSaveLevelDate = useCallback(withToast(saveLevelDate, { failed: "Couldn't save that date" }), [saveLevelDate, withToast]);
  const handleUpsertPlan = useCallback(withToast(upsertPlan, { failed: "Couldn't update the training plan" }), [upsertPlan, withToast]);

  // People and sites
  const handleDeleteApprentice = useCallback(withToast(deleteApprentice, {
    done: 'Person removed, along with all their records.', failed: "Couldn't remove that person",
  }), [deleteApprentice, withToast]);
  const handleArchive = useCallback(withToast(
    (userId) => setArchived(userId, true),
    { done: 'Archived — their records are kept and their login is switched off.', failed: "Couldn't archive that person" },
  ), [setArchived, withToast]);
  const handleRestore = useCallback(withToast(
    (userId) => setArchived(userId, false),
    { done: 'Restored — they can sign in again.', failed: "Couldn't restore that person" },
  ), [setArchived, withToast]);
  const handleAssignTrainer = useCallback(withToast(assignTrainer, { done: 'Assignments saved.', failed: "Couldn't save assignments" }), [assignTrainer, withToast]);
  const handleUpdateUser = useCallback(withToast(updateUser, { done: 'Details saved.', failed: "Couldn't save those details" }), [updateUser, withToast]);
  const handleAddSite = useCallback(withToast(addSite, { done: 'Site added.', failed: "Couldn't add that site" }), [addSite, withToast]);
  const handleUpdateSite = useCallback(withToast(updateSite, { done: 'Site updated.', failed: "Couldn't update that site" }), [updateSite, withToast]);
  const handleDeleteSite = useCallback(withToast(deleteSite, { done: 'Site deleted.', failed: "Couldn't delete that site" }), [deleteSite, withToast]);

  // Trade school / Kangan logs
  const handleAddTS = useCallback(withToast(addTradeSchoolLog, { done: 'Hours logged.', failed: "Couldn't log those hours" }), [addTradeSchoolLog, withToast]);
  const handleDeleteTS = useCallback(withToast(deleteTradeSchoolLog, { done: 'Entry deleted.', failed: "Couldn't delete that entry" }), [deleteTradeSchoolLog, withToast]);
  const handleAddKangan = useCallback(withToast(addKanganLog, { done: 'Visit logged.', failed: "Couldn't log that visit" }), [addKanganLog, withToast]);
  const handleDeleteKangan = useCallback(withToast(deleteKanganLog, { done: 'Visit deleted.', failed: "Couldn't delete that visit" }), [deleteKanganLog, withToast]);

  const handleEvidencePDF = useCallback(async (entry) => {
    const appr = allProfiles.find(p => p.id === entry.apprentice_id);
    // The apprentice's own trainer — not simply the first trainer in the org,
    // which is what used to get printed on the signed record.
    const trainer = allProfiles.find(p => p.role === 'trainer' && (p.assigned_to || []).includes(entry.apprentice_id))
      || allProfiles.find(p => p.role === 'trainer');
    const unit = getUnit(entry.unit_code);
    // Entries carry up to 30 photos — show progress so nobody hammers the button
    // thinking it failed, and never leave the overlay stuck if something goes wrong.
    setPdfProgress({ done: 0, total: 0 });
    try {
      await generateEvidencePDF(
        entry, appr?.name, trainer?.name, appr?.student_number, unit?.name,
        (done, total) => setPdfProgress({ done, total }),
      );
    } catch (e) {
      console.error('PDF generation failed:', e);
      toast.error(`Couldn't build the PDF — ${e?.message || e}. Your evidence and photos are safe.`);
    } finally {
      setPdfProgress(null);
    }
  }, [allProfiles, getUnit, toast]);

  // Open an existing entry (draft, pending, approved or rejected) in the evidence form
  const handleEditEvidence = useCallback((entry) => {
    setEditingDraft(entry);
    setPresetUnit(null);
    setShowAdd(true);
  }, []);

  // Start a new entry, optionally against a unit chosen on the dashboard
  const handleAddEvidence = useCallback((unitCode) => {
    setEditingDraft(null);
    setPresetUnit(typeof unitCode === 'string' ? unitCode : null);
    setShowAdd(true);
  }, []);

  // ── Pending count for badge ──
  // Counts only the people this user is responsible for, so the badge matches the
  // list they land on. Falls back to everyone when no caseload is assigned.
  const pendingCount = useMemo(() => {
    const mine = new Set(myApprentices.map(a => a.id));
    return filteredEvidence.filter(e =>
      e.status === 'pending' && (mine.size === 0 || mine.has(e.apprentice_id))
    ).length;
  }, [filteredEvidence, myApprentices]);

  // ── Redeeming an emailed link ──
  // Sits ahead of the auth gate: the visitor has no session yet, and showing them
  // the login page would waste the one-time token in the URL.
  if (location.pathname === '/auth/confirm') {
    return <div className="app"><AuthConfirmPage /></div>;
  }

  // ── Loading ──
  if (authLoading) {
    return <div className="app"><div className="spinner"><div className="spin" /> Loading…</div></div>;
  }

  if (!session || !profile) {
    return <div className="app"><LoginPage onLogin={login} onForgot={sendPasswordReset} /></div>;
  }

  // Archived accounts are banned at the auth layer, but someone already signed in
  // keeps a valid token until it expires — stop them here rather than letting
  // them log evidence that nobody is watching any more.
  if (profile.archived_at) {
    return (
      <div className="app">
        <div className="login-bg">
          <div className="login-card" style={{ textAlign: 'center' }}>
            <div className="login-title">Signed<em>Off</em></div>
            <div className="login-sub">Account archived</div>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7, margin: '18px 0 22px' }}>
              This account has been archived, so it can no longer be used to log
              evidence. Your records have been kept — speak to your employer if
              you think this is a mistake.
            </p>
            <button className="btn" style={{ width: '100%' }} onClick={logout}>Sign Out</button>
          </div>
        </div>
      </div>
    );
  }

  if (!profile.password_set) {
    return <div className="app"><SetPasswordPage profile={profile} onSetPassword={setPassword} /></div>;
  }

  // ── Nav items based on role ──
  // Everyone who reviews gets one Evidence page — log and queue combined — so the
  // badge and the reading list live in the same place.
  const isReviewer = ['trainer', 'employer', 'supervisor'].includes(profile.role);
  const navItems = [
    { id: 'dashboard', icon: '⊞', label: 'Dashboard' },
    isReviewer
      ? { id: 'evidence', icon: '📋', label: 'Evidence & Review', badge: pendingCount || null }
      : { id: 'evidence', icon: '📋', label: 'Evidence Log' },
    { id: 'units', icon: '🏆', label: 'Unit Progress' },
    { id: 'tradeschool', icon: '🏫', label: 'Trade & Kangan' },
    ...(profile.role === 'trainer' ? [
      { id: 'trainingplan', icon: '📐', label: 'Training Plans' },
      { id: 'bulkupload', icon: '📤', label: 'Bulk Upload' },
      { id: 'reports', icon: '📊', label: 'Reports' },
    ] : []),
    ...(profile.role === 'employer' ? [
      { id: 'workplacesignoff', icon: '🔧', label: 'Workplace Sign-off' },
      { id: 'trainingplan', icon: '📐', label: 'Training Plans' },
      { id: 'bulkupload', icon: '📤', label: 'Bulk Upload' },
      { id: 'reports', icon: '📊', label: 'Reports' },
      { id: 'apprentices', icon: '👥', label: 'Manage People' },
    ] : []),
    ...(profile.role === 'supervisor' ? [
      { id: 'workplacesignoff', icon: '🔧', label: 'Workplace Sign-off' },
      { id: 'reports', icon: '📊', label: 'Reports' },
    ] : []),
    ...(profile.role === 'apprentice' ? [
      { id: 'trainingplan', icon: '📐', label: 'Training Plan' },
      { id: 'reports', icon: '📊', label: 'Reports' },
    ] : []),
    { id: 'help', icon: '❓', label: 'Help' },
    { id: 'feedback', icon: '💬', label: 'Feedback' },
    { id: 'account', icon: '⚙', label: 'Account', section: 'account' },
  ];

  // ── Shared page props (FIX #14: uses filteredEvidence) ──
  const sharedProps = {
    profile,
    evidence: filteredEvidence,
    myApprentices,
    selApprId: activeApprId,
    setSelApprId,
    allProfiles,
    units: allUnits,
    trainingPlans,
    signoffs: unitSignoffs,
    workplaceSignoffs,
    knowledgeUnits,
    levelDates,
    tradeSchoolLogs,
    kanganLogs,
    getUnitName,
    sites,
    getSiteName,
  };

  // ── Loading skeleton ──
  const loadingEl = (
    <div className="spinner" style={{ padding: '60px 0' }}>
      <div className="spin" /> Loading data…
    </div>
  );

  return (
    <>
      <div className="app">
        <Topbar profile={profile} onLogout={logout} />
        <div className="main">
          <Sidebar items={navItems} active={currentView} onChange={setView} />
          <div className="content">
            <ErrorBoundary>
              {dataLoading ? loadingEl : (
                <Routes>
                  <Route path="/" element={<DashboardPage {...sharedProps} onAddEvidence={handleAddEvidence} />} />
                  <Route path="/evidence" element={isReviewer
                    ? <EvidenceReviewPage {...sharedProps} onApprove={handleApprove} onReject={handleReject} onUpdateNote={handleUpdateNote} onEditEvidence={handleEditEvidence} onLightbox={setLightbox} onPDF={handleEvidencePDF} />
                    : <EvidenceLogPage {...sharedProps} onAddEvidence={handleAddEvidence} onEditDraft={handleEditEvidence} onEditEvidence={handleEditEvidence} onDeleteEvidence={handleDeleteEvidence} onLightbox={setLightbox} onPDF={handleEvidencePDF} />} />
                  <Route path="/units" element={<UnitProgressPage {...sharedProps} onSaveLevelDate={handleSaveLevelDate} onToggleSignoff={handleToggleSignoff} onToggleWorkplace={handleToggleWorkplace} onToggleKnowledge={handleToggleKnowledge} />} />
                  <Route path="/workplace-signoff" element={<WorkplaceSignoffPage {...sharedProps} onToggleWorkplace={handleToggleWorkplace} />} />
                  <Route path="/tradeschool" element={<TradeSchoolPage {...sharedProps} onAddTS={handleAddTS} onDeleteTS={handleDeleteTS} onAddKangan={handleAddKangan} onDeleteKangan={handleDeleteKangan} />} />
                  {/* The separate queue is gone — send old links and bookmarks
                      to the combined page. */}
                  <Route path="/review" element={<Navigate to="/evidence" replace />} />
                  <Route path="/training-plan" element={<TrainingPlanPage {...sharedProps} onUpsertPlan={handleUpsertPlan} />} />
                  {/* Bulk upload keeps the raw hooks: it reports its own per-row
                      tally, and wrapping these would fire a toast per CSV row. */}
                  <Route path="/bulk-upload" element={<BulkUploadPage {...sharedProps} onUpsertPlan={upsertPlan} onToggleKnowledge={toggleKnowledge} onToggleSignoff={toggleUnitSignoff} onAddTS={addTradeSchoolLog} />} />
                  {/* Reports can still reach people who have been archived —
                      that retention is the whole point of archiving. */}
                  <Route path="/reports" element={<ReportsPage {...sharedProps} evidence={evidenceForReports} myApprentices={[...myApprentices, ...archivedApprentices]} />} />
                  <Route path="/apprentices" element={<ApprenticesPage {...sharedProps} archivedApprentices={archivedApprentices} onInvite={inviteUser} onGetLink={getSignInLink} onDelete={handleDeleteApprentice} onArchive={handleArchive} onRestore={handleRestore} onAssignTrainer={handleAssignTrainer} onUpdateUser={handleUpdateUser} onAddSite={handleAddSite} onUpdateSite={handleUpdateSite} onDeleteSite={handleDeleteSite} />} />
                  <Route path="/help" element={<HelpPage profile={profile} />} />
                  <Route path="/feedback" element={<FeedbackPage profile={profile} />} />
                  <Route path="/account" element={<AccountPage profile={profile} onSetPassword={setPassword} onUpdateProfile={updateProfile} />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              )}
            </ErrorBoundary>
          </div>
        </div>
        <BottomNav items={navItems} active={currentView} onChange={setView} />
      </div>

      {showAdd && (
        <ErrorBoundary>
          <AddEvidenceModal
            profile={profile}
            units={allUnits}
            evidence={evidence}
            trainingPlans={trainingPlans}
            activeApprId={activeApprId}
            editingDraft={editingDraft}
            presetUnitCode={presetUnit}
            onClose={() => { setShowAdd(false); setEditingDraft(null); setPresetUnit(null); }}
            onSave={async (entry) => {
              await addEvidence(entry);
              setShowAdd(false);
              setEditingDraft(null);
              setPresetUnit(null);
            }}
            onSaveDraft={saveDraft}
            onUpdateDraft={updateDraft}
          />
        </ErrorBoundary>
      )}

      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Evidence" />
        </div>
      )}

      {pdfProgress && (
        <div className="pdf-overlay">
          <div className="pdf-overlay-box">
            <div className="spin" />
            <div className="pdf-overlay-title">Building your PDF…</div>
            <div className="pdf-overlay-sub">
              {pdfProgress.total > 0
                ? `Adding photo ${pdfProgress.done} of ${pdfProgress.total}`
                : 'Preparing evidence…'}
            </div>
            <div className="pdf-overlay-note">
              Large entries can take a minute — please keep this page open.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
